import { zip } from 'fflate';
import sanitizeFilename from 'sanitize-filename';
import { parseTrsx } from '@newtontechnologies/beey-trsx-js';
import { downloadSubtitles, downloadSubtitlesFromLabeled, fetchExport, fetchTrsx, } from 'api/project-api';
import { BeeyLocale } from 'libs/locales/locale';
export const BULK_EXPORT_FORMATS = ['docx', 'pdf', 'trsx', 'subtitles'];
export const SUBTITLE_FORMATS_WITHOUT_FORMATTING = ['srt_FB', 'stl', 'ttml'];
class ProjectExportError extends Error {
    constructor(reason) {
        super(reason);
        this.name = 'ProjectExportError';
        this.reason = reason;
    }
}
const EXPORT_FAILURE_BY_STATE = {
    InProgress: 'not-completed',
    Failed: 'transcription-failed',
    Canceled: 'transcription-canceled',
    None: 'no-transcription',
};
const CONCURRENT_DOWNLOADS = 3;
const FALLBACK_LANGUAGE = BeeyLocale.const('en-US');
const configuredLanguage = (project) => {
    var _a;
    const code = (_a = project.transcriptionConfig) === null || _a === void 0 ? void 0 : _a.language;
    if (code === null || code === undefined) {
        return null;
    }
    return BeeyLocale.fromCode(code);
};
const fetchTrsxContent = async (connection, project) => {
    let trsx = await fetchTrsx(connection, project.id, 'currentTrsx');
    if (trsx.isFail() && trsx.err() === 'not-found') {
        trsx = await fetchTrsx(connection, project.id, 'initialTrsx');
    }
    if (!trsx.isSuccess()) {
        throw new ProjectExportError(trsx.err() === 'not-found' ? 'not-found' : 'unexpected');
    }
    return trsx.get().content;
};
const parseProjectTrsx = (content, project) => {
    var _a;
    const parsedTrsx = parseTrsx(content);
    if (!parsedTrsx.success) {
        throw new ProjectExportError('unexpected');
    }
    const languageCode = parsedTrsx.value.getTrsx().transcriptionMeta.language;
    const language = languageCode === undefined ? null : BeeyLocale.fromCode(languageCode);
    return {
        captionsMeta: parsedTrsx.value.getCaptionsMeta(),
        language: (_a = language !== null && language !== void 0 ? language : configuredLanguage(project)) !== null && _a !== void 0 ? _a : FALLBACK_LANGUAGE,
    };
};
const toSubtitleFailure = (error) => (error === 'invalid-file-format' ? 'invalid-subtitle-format' : 'subtitles-failed');
const subtitlesExtension = (options, language) => {
    if (options.subtitlesFormat === 'srt_FB') {
        return `${language.code.replace('-', '_')}.srt`;
    }
    if (options.subtitlesFormat === 'stl_apa') {
        return 'stl';
    }
    return options.subtitlesFormat;
};
const fetchSubtitles = async (connection, project, options) => {
    const trsx = parseProjectTrsx(await fetchTrsxContent(connection, project), project);
    const apiFormat = options.subtitlesFormat === 'srt_FB' ? 'srt' : options.subtitlesFormat;
    const rightToLeft = trsx.language.isRightToLeft();
    const result = trsx.captionsMeta !== undefined
        ? await downloadSubtitlesFromLabeled(connection, project, apiFormat, SUBTITLE_FORMATS_WITHOUT_FORMATTING.includes(options.subtitlesFormat) ? 'DisableAll' : 'AllowAll', trsx.captionsMeta.display.style.text.upperCase, trsx.captionsMeta.display.contentLayout.trimLines, rightToLeft)
        : await downloadSubtitles(connection, project, apiFormat, options.maxLineLength, options.speakerSignPlacement, options.pauseBetweenCaptions, options.autofillPauseBetweenCaptions, options.upperCaseAllText, rightToLeft);
    if (!result.isSuccess()) {
        throw new ProjectExportError(toSubtitleFailure(result.err()));
    }
    return {
        content: new Uint8Array(await result.get().arrayBuffer()),
        extension: subtitlesExtension(options, trsx.language),
    };
};
const fetchProjectFile = async (connection, project, options) => {
    if (options.format === 'subtitles') {
        return fetchSubtitles(connection, project, options.subtitles);
    }
    if (options.format === 'trsx') {
        const content = await fetchTrsxContent(connection, project);
        return { content: new TextEncoder().encode(content), extension: 'trsx' };
    }
    const trsx = parseProjectTrsx(await fetchTrsxContent(connection, project), project);
    const blob = await fetchExport(connection, project, options.format, options.withTimeStamps, trsx.language.isRightToLeft(), options.exportTemplateId);
    return { content: new Uint8Array(await blob.arrayBuffer()), extension: options.format };
};
const toFailure = (error) => {
    if (error instanceof ProjectExportError) {
        return error.reason;
    }
    return 'unexpected';
};
const uniqueFilename = (usedNames, project, extension) => {
    const base = sanitizeFilename(project.description.name) || `project-${project.id}`;
    let candidate = `${base}.${extension}`;
    let counter = 2;
    while (usedNames.has(candidate)) {
        candidate = `${base} (${counter}).${extension}`;
        counter += 1;
    }
    usedNames.add(candidate);
    return candidate;
};
const buildZip = (files) => new Promise((resolve, reject) => {
    zip(files, { level: 6 }, (error, data) => {
        if (error !== null) {
            reject(error);
            return;
        }
        resolve(new Blob([data], { type: 'application/zip' }));
    });
});
const runWithConcurrency = async (items, limit, worker) => {
    let nextIndex = 0;
    const runNext = async () => {
        if (nextIndex >= items.length) {
            return;
        }
        const item = items[nextIndex];
        nextIndex += 1;
        await worker(item);
        await runNext();
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runNext));
};
export const bulkExportProjects = async (connection, projects, options, onProgress) => {
    const files = {};
    const usedNames = new Set();
    const failed = [];
    let finishedCount = 0;
    await runWithConcurrency(projects, CONCURRENT_DOWNLOADS, async (project) => {
        if (project.processingState !== 'Completed') {
            failed.push({ project, reason: EXPORT_FAILURE_BY_STATE[project.processingState] });
        }
        else {
            try {
                const file = await fetchProjectFile(connection, project, options);
                files[uniqueFilename(usedNames, project, file.extension)] = file.content;
            }
            catch (error) {
                failed.push({ project, reason: toFailure(error) });
            }
        }
        finishedCount += 1;
        onProgress(finishedCount);
    });
    const exportedCount = Object.keys(files).length;
    return {
        zipBlob: exportedCount === 0 ? null : await buildZip(files),
        exportedCount,
        failed,
    };
};
